import { ADMIN_USER_DELETE_PATH, ME_PATH } from './account-deletion';
import { EMAIL_CODE_LOCK_PERMISSION, EMAIL_CODE_LOCK_ROUTE_PATH } from './email-code-lock';
import { IMPERSONATION_HEADER } from './impersonation';
import { MIDDLEWARE_SECRET_COPY_PATH, MODULE_SETTINGS_PATH } from './middleware-settings';
import type { MiddlewareModuleId } from './middlewares';
import { modulePermission } from './modules';
import type { AdminPermission } from './permissions';
import { VIEW_AS_HEADER } from './dev-view-as';
import { POWERSCHOOL_SECTION_STUDENTS_PATH } from './powerschool-class-list';
import { POWERSCHOOL_TIMETABLE_WEEK_PATH } from './powerschool-timetable';
import {
  ODOO_ABSENCES_DAY_PATH,
  ODOO_LEAVE_ALLOCATION_PATH,
  ODOO_LEAVE_OVERVIEW_PATH,
  ODOO_REQUEST_LEAVE_PATH,
  ODOO_REQUEST_TYPES_PATH,
} from './odoo-timeoff';

/**
 * Route tables: the single source of truth for every HTTP route, its listener and its
 * auth rule. Servers build their auth hooks from these tables, and tests walk them
 * exhaustively — a route that isn't listed here is a bug (pattern from Mantle Watch).
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type RouteAuth =
  /** No credentials. */
  | { kind: 'public' }
  /** Valid access token from the school's own realm; no specific permission. */
  | { kind: 'user' }
  /** Access token + at least one of these permissions (union of the user's roles). */
  | { kind: 'permission'; anyOf: readonly AdminPermission[] }
  /** Server-to-server request signed with the enrollment credential (see signing.ts). */
  | { kind: 'signed'; keyId: 'tenant-private-admin' | 'tenant-push-relay' }
  /** One-time enrollment token in the body. */
  | { kind: 'enrollment-token' }
  /** Bearer token from the central teachpartner-host realm. */
  | { kind: 'host-admin' }
  /**
   * Middleware module route (Phase 2 plug-ins), e.g. module 'powerschool.timetable'. Signed-in
   * school user; the middleware must be allowed and the module enabled (404 otherwise, checked
   * before the permission); then the user must hold `module.<module>.use` (403). A `writes`
   * route also rejects a principal whose only roles are in the Users category.
   */
  | { kind: 'module'; module: MiddlewareModuleId }
  /**
   * The seeded `tenant_admin` role itself (not a permission another role could hold). For the
   * few actions only the school's owner may take, e.g. copying a stored vendor credential.
   */
  | { kind: 'tenant-admin' }
  /**
   * Incoming webhook with a shared secret header (GitLab `X-Gitlab-Token`), checked in constant
   * time. Only a hint: the handler acts on nothing it can't verify itself (LIVE L3).
   */
  | { kind: 'webhook'; source: 'gitlab' };

export type Listener = 'public' | 'private';

/** `middleware-write`: a middleware module write, per user (e.g. an Odoo leave request). */
export type RateLimitKey =
  | 'invite'
  | 'push'
  | 'enroll'
  | 'upload'
  | 'secret-copy'
  | 'webhook'
  | 'middleware-write'
  /** Deleting an account (DELETE /api/v1/me): a few per user per hour. */
  | 'account-delete'
  | 'default';

export interface RouteSpec {
  method: HttpMethod;
  /** Fastify-style path, e.g. /api/v1/admin/roles/:roleId */
  path: string;
  listener: Listener;
  auth: RouteAuth;
  /** True for any route that changes state; used by the "user role can never write" test. */
  writes: boolean;
  /** Admin/ops actions that must produce an audit_log entry. */
  audited: boolean;
  rateLimit?: RateLimitKey;
}

const PUBLIC = { kind: 'public' } as const;
const USER = { kind: 'user' } as const;
const perm = (...anyOf: AdminPermission[]): RouteAuth => ({ kind: 'permission', anyOf });
const OPS = { kind: 'signed', keyId: 'tenant-private-admin' } as const;
const mod = (module: MiddlewareModuleId): RouteAuth => ({ kind: 'module', module });
const HOST = { kind: 'host-admin' } as const;
const TENANT_ADMIN = { kind: 'tenant-admin' } as const;

const r = (
  method: HttpMethod,
  path: string,
  auth: RouteAuth,
  extra: Partial<Pick<RouteSpec, 'listener' | 'audited' | 'rateLimit'>> = {},
): RouteSpec => ({
  method,
  path,
  auth,
  listener: extra.listener ?? 'public',
  writes: method !== 'GET',
  audited: extra.audited ?? false,
  ...(extra.rateLimit ? { rateLimit: extra.rateLimit } : {}),
});

/** tenant-api (brief §8.1, §8.2). Public listener :3000 behind Caddy; private :3001 on the tailnet. */
export const TENANT_ROUTES: readonly RouteSpec[] = [
  r('GET', '/api/v1/health', PUBLIC),
  r('GET', '/api/v1/manifest', PUBLIC),
  r('GET', '/api/v1/branding/logo', PUBLIC),
  r('GET', '/api/v1/branding.css', PUBLIC),
  r('GET', ME_PATH, USER),
  // Self-delete (D10, review S2): typed confirmation and a recent sign-in; blocked while
  // impersonating (a write). The handler also refuses the school's last Tenant Admin.
  r('DELETE', ME_PATH, USER, { audited: true, rateLimit: 'account-delete' }),
  r('POST', '/api/v1/devices', USER),
  r('DELETE', '/api/v1/devices/:expoPushToken', USER),
  r('GET', '/api/v1/notifications', USER),
  // Inbox read state: user writes, not audited (blocked while impersonating, like every write).
  r('POST', '/api/v1/notifications/read-all', USER),
  r('POST', '/api/v1/notifications/:notificationId/read', USER),
  r('POST', '/api/v1/notifications/:notificationId/unread', USER),
  // Inbox tidy-up, per user: archive, restore, archive every read one, delete from Archived.
  r('POST', '/api/v1/notifications/archive-read', USER),
  r('POST', '/api/v1/notifications/:notificationId/archive', USER),
  r('POST', '/api/v1/notifications/:notificationId/unarchive', USER),
  r('DELETE', '/api/v1/notifications/:notificationId', USER),
  r('GET', '/api/v1/admin/branding', perm('branding.manage')),
  r('PUT', '/api/v1/admin/branding', perm('branding.manage'), {
    audited: true,
    rateLimit: 'upload',
  }),
  r('PUT', '/api/v1/admin/branding/logo', perm('branding.manage'), {
    audited: true,
    rateLimit: 'upload',
  }),
  r('DELETE', '/api/v1/admin/branding/logo', perm('branding.manage'), { audited: true }),
  r('GET', '/api/v1/admin/auth-providers', perm('auth_providers.manage')),
  r('PUT', '/api/v1/admin/auth-providers', perm('auth_providers.manage'), { audited: true }),
  r('GET', '/api/v1/admin/modules', perm('modules.manage')),
  r('PUT', '/api/v1/admin/modules', perm('modules.manage'), { audited: true }),
  // Module settings (2026-10-11): per module, e.g. PowerSchool · Timetable bell periods.
  r('GET', MODULE_SETTINGS_PATH, perm('modules.manage')),
  r('PUT', MODULE_SETTINGS_PATH, perm('modules.manage'), { audited: true }),
  r(
    'GET',
    '/api/v1/admin/roles',
    perm('roles.create', 'roles.edit', 'roles.delete', 'users.assign_roles'),
  ),
  r('POST', '/api/v1/admin/roles', perm('roles.create'), { audited: true }),
  r('PATCH', '/api/v1/admin/roles/:roleId', perm('roles.edit'), { audited: true }),
  r('DELETE', '/api/v1/admin/roles/:roleId', perm('roles.delete'), { audited: true }),
  r('GET', '/api/v1/admin/permissions', perm('roles.create', 'roles.edit')),
  r('GET', '/api/v1/admin/users', perm('users.read')),
  r('POST', '/api/v1/admin/users', perm('users.invite'), { audited: true, rateLimit: 'invite' }),
  r('PATCH', '/api/v1/admin/users/:userId', perm('users.assign_roles'), { audited: true }),
  // Delete another user (same service as self-delete): Tenant Admin role only, rank rule.
  r('DELETE', ADMIN_USER_DELETE_PATH, TENANT_ADMIN, {
    audited: true,
    rateLimit: 'account-delete',
  }),
  r('POST', '/api/v1/admin/push/test', perm('push.test'), { audited: true, rateLimit: 'push' }),
  r('GET', '/api/v1/admin/notification-settings', perm('notifications.manage')),
  r('PUT', '/api/v1/admin/notification-settings', perm('notifications.manage'), { audited: true }),
  r('GET', '/api/v1/admin/audit', perm('audit.read')),
  r('POST', '/api/v1/admin/impersonation', perm('users.impersonate'), { audited: true }),
  r('DELETE', '/api/v1/admin/impersonation', perm('users.impersonate'), { audited: true }),
  // Email-code lockout: read / unlock one user (email-code-lock.ts; owner decision 2026-10-09 #5).
  // The handler adds the rank rule with the Tenant Admin exception (canManageEmailCodeLock).
  r('GET', EMAIL_CODE_LOCK_ROUTE_PATH, perm(EMAIL_CODE_LOCK_PERMISSION)),
  r('DELETE', EMAIL_CODE_LOCK_ROUTE_PATH, perm(EMAIL_CODE_LOCK_PERMISSION), { audited: true }),
  // Plug-in middlewares (Phase 2): settings and "Test connection" per middleware.
  r('GET', '/api/v1/admin/middlewares', perm('modules.manage')),
  r('GET', '/api/v1/admin/middlewares/:middlewareId/settings', perm('modules.manage')),
  r('PUT', '/api/v1/admin/middlewares/:middlewareId/settings', perm('modules.manage'), {
    audited: true,
  }),
  r('POST', '/api/v1/admin/middlewares/:middlewareId/test', perm('modules.manage'), {
    audited: true,
  }),
  // Copy a stored credential to the clipboard (owner decision 2026-10-11): Tenant Admin role
  // only, POST (never cached or in a URL), audited, its own rate limit. It counts as a write so
  // an impersonating admin is refused (view-only impersonation).
  r('POST', MIDDLEWARE_SECRET_COPY_PATH, TENANT_ADMIN, {
    audited: true,
    rateLimit: 'secret-copy',
  }),
  // Middleware module routes: /api/v1/<middleware>/<module>/… (first segment never reserved).
  r('GET', POWERSCHOOL_TIMETABLE_WEEK_PATH, mod('powerschool.timetable')),
  // Class list of one of the teacher's own sections (names pass through, never stored).
  r('GET', POWERSCHOOL_SECTION_STUDENTS_PATH, mod('powerschool.timetable')),
  // Odoo Time Off (plan §5): the signed-in person's own data; Who is absent for designated roles.
  r('GET', ODOO_LEAVE_OVERVIEW_PATH, mod('odoo.leave')),
  r('GET', ODOO_LEAVE_ALLOCATION_PATH, mod('odoo.leave')),
  r('GET', ODOO_REQUEST_TYPES_PATH, mod('odoo.request')),
  // Creates an hr.leave in Odoo: a write (Users-only and view-as refused), audited.
  r('POST', ODOO_REQUEST_LEAVE_PATH, mod('odoo.request'), {
    audited: true,
    rateLimit: 'middleware-write',
  }),
  // Shows who is on (sick) leave: every view audited (who looked, which day).
  r('GET', ODOO_ABSENCES_DAY_PATH, mod('odoo.absences'), { audited: true }),
  r('GET', '/private/v1/status', OPS, { listener: 'private' }),
  r('PUT', '/private/v1/branding', OPS, { listener: 'private', audited: true }),
  r('PUT', '/private/v1/modules', OPS, { listener: 'private', audited: true }),
  r('PUT', '/private/v1/tenant-status', OPS, { listener: 'private', audited: true }),
  r('POST', '/private/v1/devices/invalid', OPS, { listener: 'private' }),
  // Host Admin's middleware allow-list for this school (re-sent by central until in sync).
  r('PUT', '/private/v1/middlewares', OPS, { listener: 'private', audited: true }),
];

/** central-api (brief §10). Public listener behind Caddy; private listener on the tailnet only. */
export const CENTRAL_ROUTES: readonly RouteSpec[] = [
  r('GET', '/v1/directory', PUBLIC),
  r('GET', '/v1/health', PUBLIC),
  r(
    'POST',
    '/v1/tenants/enroll',
    { kind: 'enrollment-token' },
    { listener: 'private', audited: true, rateLimit: 'enroll' },
  ),
  r(
    'POST',
    '/v1/push/send',
    { kind: 'signed', keyId: 'tenant-push-relay' },
    { listener: 'private', rateLimit: 'push' },
  ),
  r(
    'POST',
    '/v1/push/receipts',
    { kind: 'signed', keyId: 'tenant-push-relay' },
    { listener: 'private', rateLimit: 'push' },
  ),
  r('GET', '/v1/admin/tenants', { kind: 'host-admin' }),
  r('POST', '/v1/admin/tenants', { kind: 'host-admin' }, { audited: true }),
  r('GET', '/v1/admin/tenants/:code', { kind: 'host-admin' }),
  r('PATCH', '/v1/admin/tenants/:code', { kind: 'host-admin' }, { audited: true }),
  r('DELETE', '/v1/admin/tenants/:code', { kind: 'host-admin' }, { audited: true }),
  r('POST', '/v1/admin/tenants/:code/enrollment-token', { kind: 'host-admin' }, { audited: true }),
  // Resend the first Tenant Admin's welcome email (central SMTP), only before enrollment.
  r('POST', '/v1/admin/tenants/:code/welcome-email', { kind: 'host-admin' }, { audited: true }),
  r(
    'DELETE',
    '/v1/admin/tenants/:code/enrollment-token',
    { kind: 'host-admin' },
    { audited: true },
  ),
  r('GET', '/v1/admin/tenants/:code/health', { kind: 'host-admin' }),
  r('PUT', '/v1/admin/tenants/:code/ops/branding', { kind: 'host-admin' }, { audited: true }),
  r('PUT', '/v1/admin/tenants/:code/ops/modules', { kind: 'host-admin' }, { audited: true }),
  // Middleware allow-list per school (Phase 2 plug-ins): held on central, pushed to the school.
  r('GET', '/v1/admin/tenants/:code/middlewares', HOST),
  r('PUT', '/v1/admin/tenants/:code/middlewares', HOST, { audited: true }),
  r('POST', '/v1/admin/gateway/reload', { kind: 'host-admin' }, { audited: true }),
  r('GET', '/v1/admin/push/stats', { kind: 'host-admin' }),
  r('GET', '/v1/admin/audit', { kind: 'host-admin' }),
  // Central updater (LIVE L3): state and history, and the Host Admin's re-runs.
  r('GET', '/v1/admin/updates', HOST),
  r('GET', '/v1/admin/updates/runs/:id', HOST),
  r('POST', '/v1/admin/updates/check', HOST, { audited: true }),
  r('POST', '/v1/admin/updates/deploy', HOST, { audited: true }),
  r('POST', '/v1/admin/updates/rollback', HOST, { audited: true }),
  // GitLab job events: a finished release-publish job hints that a release exists.
  r(
    'POST',
    '/v1/hooks/gitlab',
    { kind: 'webhook', source: 'gitlab' },
    { audited: true, rateLimit: 'webhook' },
  ),
];

export function findRoute(
  table: readonly RouteSpec[],
  method: HttpMethod,
  path: string,
): RouteSpec | undefined {
  return table.find((route) => route.method === method && route.path === path);
}

/** True when a holder of these permissions passes the route's permission rule. */
export function permitsRoute(route: RouteSpec, held: ReadonlySet<string>): boolean {
  switch (route.auth.kind) {
    case 'public':
    case 'user':
      return true;
    case 'permission':
      return route.auth.anyOf.some((p) => held.has(p));
    case 'module':
      return held.has(modulePermission(route.auth.module));
    default:
      return false;
  }
}

/**
 * Request headers the app may send to tenant-api cross-origin (web app `app.<root>` to
 * school `{code}.<root>`). The tenant-api CORS preflight allows exactly these; a header the
 * app adds must be listed here or the browser blocks the request.
 */
export const TENANT_CORS_ALLOWED_HEADERS: readonly string[] = [
  'authorization',
  'content-type',
  IMPERSONATION_HEADER,
  VIEW_AS_HEADER,
];

export const IMPERSONATION_ROUTE_PATH = '/api/v1/admin/impersonation';

/**
 * How a route behaves when the request carries an impersonation session (view-only design):
 * - 'as-target': runs as the impersonated user with their permissions (every GET except the impersonation route)
 * - 'actor': runs as the admin (only DELETE /admin/impersonation, to stop)
 * - 'blocked': rejected with IMPERSONATION_READ_ONLY (every other write, incl. devices)
 */
export type ImpersonationMode = 'as-target' | 'actor' | 'blocked';

export function impersonationModeFor(route: RouteSpec): ImpersonationMode {
  if (route.path === IMPERSONATION_ROUTE_PATH)
    return route.method === 'DELETE' ? 'actor' : 'blocked';
  if (route.listener === 'private') return 'blocked';
  return route.writes ? 'blocked' : 'as-target';
}
